import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRouteComment, ApiRouteCommentOutcome } from "griptonite-api-client";

import api from "../core/api";
import { useCurrentVenueUuid } from "./venues";

/**
 * The private climber comments waiting in a route's staff inbox, newest first.
 */
export const useRouteInboxCommentsQuery = (routeUuid?: string) => {
  const venueUuid = useCurrentVenueUuid();
  return useQuery({
    queryKey: ["venues", venueUuid, "routeComments", routeUuid],
    queryFn: () =>
      api.collectPages<ApiRouteComment>(() =>
        api.venues.getVenueRouteComments(venueUuid!, { route: routeUuid, archived: false }),
      ),
    enabled: !!venueUuid && !!routeUuid,
  });
};

/**
 * Moves an inbox comment to the archive as handled or ignored. The routes data
 * is refetched too, since each route carries its inbox count. That happens
 * even when the request fails, because a 409 means another staff member got
 * there first and our copy of the list is out of date.
 */
export const useArchiveRouteCommentMutation = () => {
  const venueUuid = useCurrentVenueUuid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentUuid,
      outcome,
    }: {
      commentUuid: string;
      outcome: ApiRouteCommentOutcome;
    }) => api.venues.archiveVenueRouteComment(venueUuid!, commentUuid, outcome),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["venues", venueUuid, "routeComments"] });
      queryClient.invalidateQueries({ queryKey: ["venues", venueUuid, "routes"] });
    },
  });
};
